import type { DashboardCurrencyRow, TimeseriesMonth, TimeseriesResult, TimeseriesShopEntry } from '../../api/index.ts';
import { formatLocalDateTime, pickDashboardRow } from './dashboardHelpers.ts';

export const CHART_MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** A shop the chart's multi-select can filter to -- built from the by-shop
 *  response already fetched elsewhere on the page (spec 4.5: no extra
 *  shops:read call). */
export interface ShopOption {
  id: number;
  label: string;
}

export interface ChartSeries {
  /** null is a GAP (no line drawn through that point), not a zero -- see
   *  monthDataGapFlags below (E2.1 spec 11.5). */
  grossSales: (number | null)[];
  payout: (number | null)[];
  netProfit: (number | null)[];
}

/**
 * Which of a sorted 12-entry month array are "no data" GAPS rather than
 * real zeros (E2.1 spec 11.5 fix for the round-3 "zero-cliff" complaint):
 * - any month AFTER the current calendar month, when `year` is the current
 *   calendar year (the future -- there is no server row yet, and even if
 *   one somehow arrived it must not be trusted as a real zero);
 * - months at the START or END of the 12-month array that have no data --
 *   i.e. before the first data-bearing month or after the last one (a shop
 *   that hasn't started reporting yet, or a year with no data at all).
 * Interior no-data months (surrounded by data-bearing months on both sides)
 * are NOT gaps -- they may render as a real 0 (spec: "Interior no-data
 * months in an otherwise active year may render 0").
 */
function monthDataGapFlags(hasData: readonly boolean[], year: number, now: Date): boolean[] {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const firstDataIdx = hasData.indexOf(true);
  const lastDataIdx = hasData.lastIndexOf(true);
  return hasData.map((_present, idx) => {
    const month = idx + 1;
    const isFuture = year === currentYear && month > currentMonth;
    const isEdgeGap = firstDataIdx === -1 || idx < firstDataIdx || idx > lastDataIdx;
    return isFuture || isEdgeGap;
  });
}

/** Extracts the 3 chart series (minor units) for the selected currency across
 *  all 12 months, applying the null-gap rule above (E2.1 spec 11.5) instead
 *  of the old "no data plots as 0" rule it replaced. `year` is the series'
 *  own year (the timeseries call's `year` param, not necessarily the
 *  KPI/table period) and `now` defaults to the real clock, injectable for
 *  tests. */
export function buildChartSeries(months: TimeseriesMonth[], currencyCode: string, year: number, now: Date = new Date()): ChartSeries {
  const sorted = [...months].sort((a, b) => a.month - b.month);
  const hasData = sorted.map((m) => m.by_currency.some((c) => c.currency_code === currencyCode));
  const gaps = monthDataGapFlags(hasData, year, now);

  const grossSales: (number | null)[] = [];
  const payout: (number | null)[] = [];
  const netProfit: (number | null)[] = [];
  sorted.forEach((m, idx) => {
    const row = pickDashboardRow(m.by_currency, currencyCode);
    const gap = gaps[idx];
    grossSales.push(gap ? null : row.gross_sales_minor);
    payout.push(gap ? null : row.payout_minor);
    netProfit.push(gap ? null : row.profit_minor);
  });
  return { grossSales, payout, netProfit };
}

export type ChartMetric = 'gross_sales' | 'payout' | 'net_profit';

export const CHART_METRIC_OPTIONS: ReadonlyArray<{ value: ChartMetric; label: string }> = [
  { value: 'gross_sales', label: 'Gross sales' },
  { value: 'payout', label: 'Payout' },
  { value: 'net_profit', label: 'Net profit' },
];

function metricMinor(row: DashboardCurrencyRow, metric: ChartMetric): number {
  if (metric === 'gross_sales') return row.gross_sales_minor;
  if (metric === 'payout') return row.payout_minor;
  return row.profit_minor;
}

/** Narrows a GET /reports/timeseries result to the group_by=shop shape
 *  (E2.1 spec 11.5). */
export function isGroupedTimeseries(result: TimeseriesResult): result is Extract<TimeseriesResult, { group_by: 'shop' }> {
  return 'shops' in result;
}

/** Every currency_code appearing anywhere in a GET /reports/timeseries
 *  result, ungrouped or grouped -- feeds DashboardPage's collectCurrencies
 *  union regardless of which shape the current fetch returned. */
export function timeseriesCurrencyCodes(result: TimeseriesResult): Array<string | null | undefined> {
  if (isGroupedTimeseries(result)) {
    return result.shops.flatMap((s) => s.months.flatMap((m) => m.by_currency.map((c) => c.currency_code)));
  }
  return result.months.flatMap((m) => m.by_currency.map((c) => c.currency_code));
}

export interface ChartShopSeries {
  shopId: number;
  label: string;
  data: (number | null)[];
}

/** One line per shop for the chosen metric (E2.1 spec 11.5 compare mode,
 *  2+ shops selected) -- same null-gap rule as buildChartSeries, applied
 *  per shop so a shop that started reporting mid-year doesn't draw a
 *  phantom zero before its first report. `selectedShopIds` both filters to
 *  the chosen shops AND fixes their render order (matches the multi-select
 *  order, not necessarily shop_code order). */
export function buildShopCompareSeries(
  shops: readonly TimeseriesShopEntry[],
  selectedShopIds: readonly number[],
  currencyCode: string,
  metric: ChartMetric,
  year: number,
  now: Date = new Date(),
): ChartShopSeries[] {
  const byId = new Map(shops.map((s) => [s.shop_id, s]));
  const series: ChartShopSeries[] = [];
  for (const shopId of selectedShopIds) {
    const shop = byId.get(shopId);
    if (!shop) continue;
    const sorted = [...shop.months].sort((a, b) => a.month - b.month);
    const hasData = sorted.map((m) => m.by_currency.some((c) => c.currency_code === currencyCode));
    const gaps = monthDataGapFlags(hasData, year, now);
    const data = sorted.map((m, idx) => {
      if (gaps[idx]) return null;
      return metricMinor(pickDashboardRow(m.by_currency, currencyCode), metric);
    });
    series.push({ shopId: shop.shop_id, label: `${shop.shop_name} (${shop.shop_code})`, data });
  }
  return series;
}

/** Ordered, theme-adjacent line colours (E2.1 spec 11.5: "reuse/extend the
 *  3 series colors into a small ordered list", explicitly NOT a rainbow of
 *  random hexes). The first 3 entries ARE the existing single-shop series
 *  colours (gross blue / payout yellow / net green); the rest are other
 *  semantic channels the theme already defines (info/error/warning) plus
 *  the primary/secondary "dark" shades, so a compare-mode chart with more
 *  shops than base colours still cycles through theme vocabulary rather
 *  than inventing new hues. */
export const CHART_SERIES_PALETTE: readonly string[] = [
  'var(--mui-palette-primary-main)',
  'var(--mui-palette-secondary-main)',
  'var(--mui-palette-success-main)',
  'var(--mui-palette-info-main)',
  'var(--mui-palette-error-main)',
  'var(--mui-palette-warning-main)',
  'var(--mui-palette-primary-dark)',
  'var(--mui-palette-secondary-dark)',
];

const FALLBACK_COLOR = 'var(--mui-palette-primary-main)';

export function chartColorAt(index: number): string {
  return CHART_SERIES_PALETTE[index % CHART_SERIES_PALETTE.length] ?? FALLBACK_COLOR;
}

/** True when the viewer has requested reduced motion (E2.1 spec 4.5: the
 *  chart must respect it via x-charts' skipAnimation). Guarded for jsdom /
 *  environments with no matchMedia. */
export function prefersReducedMotion(): boolean {
  if (globalThis.window === undefined || typeof globalThis.window.matchMedia !== 'function') return false;
  return globalThis.window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---- Responsive (E2.1 spec section 8, owner ruling: phones are a primary
// access mode) -- pure sizing/tick decisions the chart makes from a single
// `compact` (below sm) boolean the page computes once via useMediaQuery, so
// these are unit-testable without mounting anything or mocking matchMedia. --

/** Chart SVG height: reduced on narrow viewports (spec 8: "~240"), taller
 *  on sm and up. */
export function chartHeightPx(compact: boolean): number {
  return compact ? 240 : 320;
}

/** Fewer x-axis month ticks on narrow viewports (spec 8: "fewer axis
 *  ticks") -- every other month label; 'auto' (x-charts' own thinning) once
 *  there is room. */
export function chartTickLabelInterval(compact: boolean): 'auto' | ((value: unknown, index: number) => boolean) {
  return compact ? (_value: unknown, index: number) => index % 2 === 0 : 'auto';
}

/** Legend below the chart on narrow viewports (spec 8), top (x-charts'
 *  default) once there is room to sit beside/above the plot comfortably. */
export function chartLegendPosition(compact: boolean): { vertical: 'top' | 'bottom'; horizontal: 'center' } {
  return { vertical: compact ? 'bottom' : 'top', horizontal: 'center' };
}

// ---- Bar mode + current-month projection (dashboard money-chart rework:
// the in-progress month plotting low on a LINE read as a decline. 0-1 shop
// selected now renders grouped BARS instead, with an optional stacked
// "projected remainder" segment on the current month so it reads as
// "still filling in", not "fell off a cliff". Compare mode (2+ shops) keeps
// the existing LineChart untouched aside from the current-month masking
// below. -------------------------------------------------------------------

/** Days of the current calendar month with a COMPLETE report on file. Shop
 *  reports land end-of-day, so today itself is never a complete day; floored
 *  at 1 so a fresh month-start never divides by zero downstream. `now`
 *  injectable, default the real clock -- same pattern as buildChartSeries. */
export function elapsedDaysInCurrentMonth(now: Date = new Date()): number {
  return Math.max(1, now.getDate() - 1);
}

/** Calendar days in `month` (1-12) of `year`. */
export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** Projects the REST of an in-progress month from its month-to-date total,
 *  at the same daily pace observed so far -- the stacked "shaded" segment on
 *  top of the current month's actual bar. Deliberately conservative: null
 *  (no projection bar) before day 8 (elapsedDays < 7, too little signal to
 *  extrapolate from) or when there is no positive MTD figure to pace from. */
export function projectRemainderMinor(mtdMinor: number, elapsedDays: number, daysInMonth: number): number | null {
  if (elapsedDays < 7 || mtdMinor <= 0) return null;
  return Math.round((mtdMinor / elapsedDays) * (daysInMonth - elapsedDays));
}

export interface ChartProjection {
  grossSales: (number | null)[];
  payout: (number | null)[];
  netProfit: (number | null)[];
}

/** The 3 metrics' projected-remainder arrays for bar mode -- all-null (no
 *  projection bar anywhere) unless `year` is the CURRENT calendar year, in
 *  which case only the current month's index carries a value, derived from
 *  that month's own actual (a null/gap actual -- e.g. a shop that has never
 *  reported -- projects nothing, there is no real MTD figure to extrapolate
 *  from). Caller gates rendering on the revenue:read permission; this helper
 *  itself has no permission awareness (pure data derivation only). */
export function buildChartProjection(series: ChartSeries, year: number, now: Date = new Date()): ChartProjection {
  const empty = (): (number | null)[] => new Array<number | null>(12).fill(null);
  if (year !== now.getFullYear()) return { grossSales: empty(), payout: empty(), netProfit: empty() };

  const idx = now.getMonth();
  const elapsed = elapsedDaysInCurrentMonth(now);
  const total = daysInMonth(year, idx + 1);
  function project(actual: (number | null)[]): (number | null)[] {
    const out = empty();
    const mtd = actual[idx];
    if (mtd !== null && mtd !== undefined) out[idx] = projectRemainderMinor(mtd, elapsed, total);
    return out;
  }
  return { grossSales: project(series.grossSales), payout: project(series.payout), netProfit: project(series.netProfit) };
}

/** "<Mon> in progress - N of M days reported" -- null when `year` isn't the
 *  current calendar year, so a past/future year's chart never shows it. */
export function currentMonthProgressCaption(year: number, now: Date = new Date()): string | null {
  if (year !== now.getFullYear()) return null;
  // NOT elapsedDaysInCurrentMonth: its floor-at-1 is a divide-by-zero guard
  // for the projection math, but as a CAPTION on the 1st it would claim
  // "1 of N days reported" when zero days are complete.
  const elapsed = Math.max(0, now.getDate() - 1);
  const total = daysInMonth(year, now.getMonth() + 1);
  return `${CHART_MONTH_LABELS[now.getMonth()]} in progress - ${elapsed} of ${total} days reported`;
}

export const PROJECTION_CAPTION = 'shaded = projected at month-to-date pace';

/** "Data as of <newest last_sync_at>" -- null when there is no by-shop data
 *  at all (an External session's fetch never runs, so its shops array is
 *  always empty), so the caption is silently absent rather than rendering a
 *  disabled state. ISO 8601 timestamps compare correctly as plain strings,
 *  no date parsing needed to find the newest one. */
export function dataAsOfCaption(lastSyncTimestamps: readonly (string | null)[]): string | null {
  const values = lastSyncTimestamps.filter((t): t is string => t !== null);
  if (values.length === 0) return null;
  let newest = values[0] ?? '';
  for (const v of values) {
    if (v > newest) newest = v;
  }
  return `Data as of ${formatLocalDateTime(newest)}`;
}

// ---- Compare mode current-month signal (spec item 3) -----------------------

/** Compare-mode's "in progress" signal for the current month. A true hollow/
 *  open marker proved impractical with x-charts' LineChart series API (no
 *  per-point fill-vs-stroke control, only a whole-series colorGetter), so
 *  the fallback route was taken instead: the current month's data point is
 *  nulled, same as the "no server row yet" gap language buildShopCompareSeries
 *  already uses for future months -- the line simply stops one point early.
 *  Only applies when `year` is the current calendar year. */
export function maskCurrentMonthForCompare(series: readonly ChartShopSeries[], year: number, now: Date = new Date()): ChartShopSeries[] {
  if (year !== now.getFullYear()) return series.map((s) => ({ ...s }));
  const idx = now.getMonth();
  return series.map((s) => {
    const data = [...s.data];
    data[idx] = null;
    return { ...s, data };
  });
}
